import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import ErrorBoundary from './ErrorBoundary'
import NotificationBell from './NotificationBell'
import { LayoutDashboard, LayoutGrid, Bot, Database, Boxes, Workflow, Plug, CreditCard, Building2, LogOut, Sun, Moon, ShieldCheck, BookOpen, Home, Route, ChevronDown, Plus, Check, Layers, Cpu, Ticket, Megaphone, Users, FileText, Inbox, BrainCircuit, FlaskConical, ShoppingBag, Webhook, BarChart3, PenTool, Radar, LayoutTemplate } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { getSession, clearSession, setSession } from '../lib/session'
import { supabase } from '../lib/supabase'
import { filterNavGroups } from '../lib/navGating'

// Merge agent-authored nav overrides (GET /site/nav) over the CODED nav groups.
// Overrides rename (label), hide (hidden), reorder (sort), or move (group_id) an item
// keyed by its `path`. Empty/failed overrides → coded nav unchanged (safe fallback).
function mergeNav(codedGroups, overrides) {
  if (!Array.isArray(overrides) || overrides.length === 0) return codedGroups
  const byPath = new Map(overrides.map((o) => [o.path, o]))
  const groups = codedGroups.map((g) => ({
    ...g,
    items: g.items.map((item) => {
      const o = byPath.get(item.path)
      if (!o) return item
      if (o.hidden) return null
      return { ...item, label: (o.label != null && o.label !== '') ? o.label : item.label, _sort: o.sort, _group: o.group_id }
    }).filter(Boolean),
  }))
  const gById = new Map(groups.map((g) => [g.id, g]))
  for (const g of groups) {
    g.items = g.items.filter((item) => {
      if (item._group && item._group !== g.id && gById.has(item._group)) { gById.get(item._group).items.push(item); return false }
      return true
    })
  }
  for (const g of groups) {
    g.items = g.items.map((item, i) => ({ item, i })).sort((a, b) => {
      const sa = a.item._sort, sb = b.item._sort
      if (sa != null && sb != null) return (sa - sb) || (a.i - b.i)
      if (sa != null) return -1
      if (sb != null) return 1
      return a.i - b.i
    }).map((x) => x.item)
  }
  return groups
}

export default function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const [session, setSessionState] = useState(getSession())
  const [theme, setTheme] = useState(() => document.documentElement.getAttribute('data-theme') || 'light')

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark'
    setTheme(next)
    document.documentElement.setAttribute('data-theme', next)
    try { localStorage.setItem('reright-theme', next) } catch {}
  }

  const [isAdmin, setIsAdmin] = useState(false)
  // Agent-editable nav overrides for this workspace (safe: [] on any failure → coded nav).
  const [navOverrides, setNavOverrides] = useState([])
  useEffect(() => {
    if (!session?.apiKey) { setNavOverrides([]); return }
    axios.get('/api/v1/marketing-intel/site/nav')
      .then((r) => setNavOverrides(Array.isArray(r.data?.overrides) ? r.data.overrides : []))
      .catch(() => setNavOverrides([]))
  }, [session?.apiKey, session?.namespaceId])

  useEffect(() => {
    const sync = () => setSessionState(getSession())
    window.addEventListener('reright-session', sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener('reright-session', sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  // Is this user a platform admin? (server-gated by ADMIN_EMAILS)
  useEffect(() => {
    if (!session?.apiKey) { setIsAdmin(false); return }
    axios.get('/api/v1/admin/me').then(r => setIsAdmin(!!r.data?.isAdmin)).catch(() => setIsAdmin(false))
  }, [session?.apiKey])

  // Which services this workspace has ACTIVATED — drives the nav entitlement gate,
  // so a customer only sees the surfaces they've turned on. Never hides on error.
  const [activeServices, setActiveServices] = useState(null) // Set<serviceKey> | null
  const [gateReady, setGateReady] = useState(false)
  useEffect(() => {
    if (!session?.apiKey || !session?.namespaceId) { setActiveServices(null); setGateReady(false); return }
    let alive = true
    axios.get(`/api/v1/tenants/${session.namespaceId}/services`)
      .then((r) => {
        if (!alive) return
        const list = Array.isArray(r.data?.services) ? r.data.services : []
        setActiveServices(new Set(list.filter((s) => s.activated).map((s) => s.service)))
        setGateReady(true)
      })
      .catch(() => { if (alive) { setActiveServices(null); setGateReady(true) } }) // error → fail open
    return () => { alive = false }
  }, [session?.apiKey, session?.namespaceId])

  // A freshly-created workspace provisions its dedicated DB in the background;
  // poll until it's ready, then clear the flag so the banner disappears.
  useEffect(() => {
    if (!session?.provisioning || !session?.namespaceId) return
    const id = setInterval(() => {
      axios.get(`/api/v1/tenants/${session.namespaceId}/stats`)
        .then(() => { const s = getSession(); if (s) { const { provisioning, ...rest } = s; setSession(rest) } })
        .catch(() => {})
    }, 5000)
    return () => clearInterval(id)
  }, [session?.provisioning, session?.namespaceId])

  // Multi-company workspace switcher: every company is its own isolated tenant.
  const [workspaces, setWorkspaces] = useState([])
  const [wsOpen, setWsOpen] = useState(false)
  const [wsBusy, setWsBusy] = useState(false)

  const loadWorkspaces = () => {
    if (!session?.apiKey) return
    axios.get('/api/v1/auth/workspaces').then(r => setWorkspaces(r.data.workspaces || [])).catch(() => {})
  }
  useEffect(loadWorkspaces, [session?.namespaceId])

  const switchTo = async (ns) => {
    if (ns === session?.namespaceId) { setWsOpen(false); return }
    setWsBusy(true)
    try {
      const r = await axios.post(`/api/v1/auth/workspaces/${ns}/token`)
      setSession({ apiKey: r.data.token, namespaceId: r.data.namespaceId, name: r.data.name, plan: r.data.plan })
      window.location.assign('/') // full reset so every page refetches under the new company
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not switch company')
      setWsBusy(false)
    }
  }

  const addCompany = async () => {
    const name = window.prompt('Name the new company / workspace')
    if (!name || !name.trim()) return
    setWsBusy(true)
    try {
      const r = await axios.post('/api/v1/auth/workspaces', { name: name.trim() })
      toast.success('Company created — setting up its private database…')
      const t = await axios.post(`/api/v1/auth/workspaces/${r.data.namespaceId}/token`)
      setSession({ apiKey: t.data.token, namespaceId: t.data.namespaceId, name: t.data.name, plan: t.data.plan, provisioning: true })
      window.location.assign('/')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not create company')
      setWsBusy(false)
    }
  }

  // Grouped, collapsible sidebar navigation. Every existing route is preserved;
  // Connections is a single entry that supersedes the old Sync / Adapters / Integrations links.
  // Items tagged with `service` (or `services` = any-of) are gated to workspaces
  // that have ACTIVATED that service. Untagged items are CORE — always visible.
  const navGroups = [
    { id: 'overview', label: 'Overview', items: [
      { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', service: 'marketing-intel' },
      { path: '/analytics', icon: BarChart3, label: 'Analytics', service: 'marketing-intel' },
    ]},
    { id: 'intelligence', label: 'Intelligence', items: [
      { path: '/marketing', icon: BrainCircuit, label: 'Marketing Brain', service: 'marketing-intel' },
      { path: '/growth-lab', icon: FlaskConical, label: 'Growth Lab', service: 'growth' },
      { path: '/studio', icon: PenTool, label: 'Marketing & SEO Studio', services: ['seo', 'marketing-intel'] },
      { path: '/seo-intelligence', icon: Radar, label: 'SEO Intelligence', service: 'seo' },
      { path: '/content', icon: FileText, label: 'Content Studio', services: ['seo', 'marketing-intel'] },
    ]},
    { id: 'engage', label: 'Engage', items: [
      { path: '/campaigns', icon: Megaphone, label: 'Campaigns', service: 'campaigns' },
      { path: '/contacts', icon: Users, label: 'Contacts', services: ['campaigns', 'commerce', 'inbox'] },
      { path: '/templates', icon: FileText, label: 'Templates', service: 'campaigns' },
      { path: '/inbox', icon: Inbox, label: 'Inbox', service: 'inbox' },
      { path: '/commerce', icon: ShoppingBag, label: 'Commerce', service: 'commerce' },
    ]},
    { id: 'build', label: 'Build', items: [
      { path: '/', icon: LayoutGrid, label: 'Services' },
      { path: '/instances', icon: Layers, label: 'Instances' },
      { path: '/agents', icon: Cpu, label: 'Agents' },
      { path: '/answers', icon: Bot, label: 'Answers · AI chatbot', service: 'answers' },
      { path: '/intake', icon: Route, label: 'Intake Systems', service: 'intake' },
      { path: '/rag', icon: Boxes, label: 'Custom RAG', service: 'rag' },
      { path: '/connections', icon: Plug, label: 'Connections' },
    ]},
    { id: 'business', label: 'Business', items: [
      { path: '/reseller', icon: Building2, label: 'White-label', service: 'reseller' },
      { path: '/billing', icon: CreditCard, label: 'Billing' },
      { path: '/governance', icon: ShieldCheck, label: 'Governance' },
      { path: '/setup', icon: Building2, label: 'Workspace' },
    ]},
  ]

  // Entitlement gate (pure logic in ../lib/navGating): admins see the whole console;
  // customers see core + only the services they've activated. Coded nav (filtered) is
  // the fallback; agent-authored overrides merge on top.
  const entitledGroups = filterNavGroups(navGroups, { isAdmin, activeServices, gateReady })
  const mergedNavGroups = mergeNav(entitledGroups, navOverrides)

  // Per-section open/closed state, persisted to localStorage. Groups default to open.
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('reright-nav-groups') || '{}') || {} }
    catch { return {} }
  })
  const toggleGroup = (id) => {
    setOpenGroups(prev => {
      const currentlyOpen = prev[id] !== false
      const next = { ...prev, [id]: !currentlyOpen }
      try { localStorage.setItem('reright-nav-groups', JSON.stringify(next)) } catch {}
      return next
    })
  }
  // A group is shown open when it holds the active route (auto-open) or its stored state isn't collapsed.
  const isGroupOpen = (group) =>
    group.items.some(i => i.path === location.pathname) || openGroups[group.id] !== false

  const signOut = () => {
    supabase?.auth.signOut().catch(() => {})
    clearSession()
    navigate('/home')
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <aside className="w-64 bg-gray-100 border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center gap-2.5">
            <div className="flex items-baseline font-display font-black text-2xl leading-none">
              <span className="text-gray-900">R</span>
              <span className="mx-1 text-gray-300 font-normal text-xl">|</span>
              <span className="text-primary-600 inline-block" style={{ transform: 'scaleX(-1)' }}>R</span>
            </div>
            <h1 className="font-display font-black tracking-tight text-gray-900 text-lg">RERIGHT</h1>
          </div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 mt-2">AI Intelligence Platform</p>
          {session?.namespaceId ? (
            <div className="relative mt-3">
              <button onClick={() => { setWsOpen(o => !o); if (!wsOpen) loadWorkspaces() }} disabled={wsBusy}
                className="w-full px-2 py-1.5 bg-green-500/10 rounded-md text-left flex items-center gap-1 hover:bg-green-500/15 transition-colors disabled:opacity-60">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold text-green-600 truncate">{session.name}</p>
                  <p className="text-[10px] text-green-600 truncate">{session.namespaceId} · {session.plan}</p>
                </div>
                <ChevronDown size={14} className="text-green-600 shrink-0" />
              </button>
              {wsOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setWsOpen(false)} />
                  <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-72 overflow-auto">
                    <p className="px-3 py-1 text-[10px] uppercase tracking-wide text-gray-400">Your companies</p>
                    {workspaces.length === 0 && <p className="px-3 py-2 text-xs text-gray-400">Loading…</p>}
                    {workspaces.map(w => {
                      const active = w.namespaceId === session.namespaceId
                      return (
                        <button key={w.namespaceId} onClick={() => switchTo(w.namespaceId)} disabled={wsBusy}
                          className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 disabled:opacity-60">
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-900 truncate">{w.name}</p>
                            <p className="text-[10px] text-gray-400 truncate">{w.plan}{w.provisioning ? ' · setting up…' : ''}</p>
                          </div>
                          {active && <Check size={14} className="text-primary-600 shrink-0" />}
                        </button>
                      )
                    })}
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button onClick={addCompany} disabled={wsBusy}
                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-primary-600 disabled:opacity-60">
                        <Plus size={14} /> <span className="text-xs font-medium">Add company</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="mt-3 px-2 py-1.5 bg-amber-500/10 rounded-md">
              <p className="text-[10px] text-amber-600">Not connected — visit Workspace</p>
            </div>
          )}
        </div>
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {mergedNavGroups.map(group => {
            const open = isGroupOpen(group)
            return (
              <div key={group.id}>
                <button onClick={() => toggleGroup(group.id)}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-gray-400 hover:text-gray-600 transition-colors">
                  <span>{group.label}</span>
                  <ChevronDown size={12} className={`shrink-0 transition-transform ${open ? '' : '-rotate-90'}`} />
                </button>
                {open && (
                  <div className="mt-1 space-y-1">
                    {group.items.map(item => {
                      if (item.soon) {
                        return (
                          <div key={item.path} title="Coming soon"
                            className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-300 cursor-default select-none">
                            <item.icon size={18} />
                            <span className="flex-1">{item.label}</span>
                            <span className="text-[9px] uppercase tracking-wide bg-gray-200 text-gray-400 px-1.5 py-0.5 rounded">Soon</span>
                          </div>
                        )
                      }
                      const active = location.pathname === item.path
                      return (
                        <Link key={item.path} to={item.path}
                          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                            active ? 'bg-primary-500/10 text-primary-600' : 'text-gray-600 hover:bg-gray-50'
                          }`}>
                          <item.icon size={18} />
                          {item.label}
                        </Link>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
          {isAdmin && (
            <>
              <Link to="/admin"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/admin' ? 'bg-primary-500/10 text-primary-600' : 'text-gray-600 hover:bg-gray-50'
                }`}>
                <ShieldCheck size={18} />
                Admin
              </Link>
              <Link to="/site-content"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/site-content' ? 'bg-primary-500/10 text-primary-600' : 'text-gray-600 hover:bg-gray-50'
                }`}>
                <LayoutTemplate size={18} />
                Site Content
              </Link>
              <Link to="/coupons"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/coupons' ? 'bg-primary-500/10 text-primary-600' : 'text-gray-600 hover:bg-gray-50'
                }`}>
                <Ticket size={18} />
                Coupons
              </Link>
            </>
          )}
        </nav>
        <div className="p-4 border-t border-gray-200 space-y-1">
          {/* ai.reright.io public homepage */}
          <Link to="/home"
            className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors w-full">
            <Home size={18} />
            Home
          </Link>
          {/* Cross-site: back to the main RERIGHT site — highlighted so it stands out. */}
          <a href="https://reright.io"
            className="flex items-center gap-3 px-3 py-2 text-sm font-semibold text-primary-600 border border-primary-600/30 hover:bg-primary-600 hover:text-white rounded-lg transition-colors w-full">
            <span className="inline-flex w-[18px] justify-center text-[15px]">↗</span>
            reright.io <span className="text-gray-400 font-normal text-xs group-hover:text-white">· main site</span>
          </a>
          <Link to="/docs"
            className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors w-full">
            <BookOpen size={18} />
            Documentation
          </Link>
          <button onClick={toggleTheme}
            className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors w-full">
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
            {theme === 'dark' ? 'Light mode' : 'Dark mode'}
          </button>
          <button onClick={signOut}
            className="flex items-center gap-3 px-3 py-2 text-sm text-gray-600 hover:text-red-600 rounded-lg transition-colors w-full">
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto p-8">
        <div className="flex justify-end mb-6">
          <NotificationBell />
        </div>
        {session?.provisioning && (
          <div className="mb-6 flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-500 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
            </span>
            <p className="text-sm text-amber-700">Your workspace is being set up — this takes a few seconds. Some features may be briefly unavailable.</p>
          </div>
        )}
        <ErrorBoundary key={location.pathname}><Outlet /></ErrorBoundary>
      </main>
    </div>
  )
}
